import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Loader2, Users, Clock, Wallet, TrendingUp } from "lucide-react";
import api from "../lib/api";
import { Avatar } from "../components/Media";

const fmtMoney = (cents, cur = "usd") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: cur.toUpperCase() }).format((cents || 0) / 100);
const fmtMonth = (k) => new Date(`${k}-01T12:00:00Z`).toLocaleDateString(undefined, { month: "short", year: "numeric" });
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "-");

function downloadCsv(d) {
  const c = (n) => ((n || 0) / 100).toFixed(2);
  const lines = [["Month", "Gross", "Stripe fees", "SovSig 1%", "Refunds", "Net", "New subscribers", "Cancelled"].join(",")];
  d.months.forEach((m) => lines.push([m.month, c(m.gross), c(-m.stripe_fees), c(-m.sovsig_fee), c(m.refunds), c(m.net), m.new_subs, m.canceled_subs].join(",")));
  lines.push("", ["Payout date", "Amount", "Currency", "Status"].join(","));
  d.payouts.forEach((p) => lines.push([p.arrival_date.slice(0, 10), c(p.amount), p.currency.toUpperCase(), p.status].join(",")));
  const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: `beacon-earnings-${new Date().toISOString().slice(0, 10)}.csv` });
  a.click();
  URL.revokeObjectURL(url);
}

const Stat = ({ icon: Icon, label, value, testid, accent }) => (
  <div className="border border-white/10 bg-[#121212] p-4 min-w-0" data-testid={testid}>
    <div className="flex items-center gap-1.5 font-body text-[11px] uppercase tracking-wider text-zinc-500 mb-2"><Icon size={13} className="text-[#14A89A] shrink-0" />{label}</div>
    <div className={`font-display text-2xl ${accent ? "text-[#14A89A]" : "text-white"}`}>{value}</div>
  </div>
);

const MonthsTable = ({ months, cur }) => (
  <div className="overflow-x-auto border border-white/10" data-testid="earnings-months-table">
    <table className="w-full font-body text-sm min-w-[640px]">
      <thead><tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500 border-b border-white/10">
        {["Month", "Gross", "Stripe fees", "SovSig 1%", "Refunds", "Your net", "New", "Cancelled"].map((h) => <th key={h} className="px-3 py-2 font-normal">{h}</th>)}
      </tr></thead>
      <tbody>
        {months.map((m) => (
          <tr key={m.month} className="border-b border-white/5 text-zinc-300" data-testid={`earnings-row-${m.month}`}>
            <td className="px-3 py-2 text-white whitespace-nowrap">{fmtMonth(m.month)}</td>
            <td className="px-3 py-2">{fmtMoney(m.gross, cur)}</td>
            <td className="px-3 py-2 text-zinc-500">{m.stripe_fees ? "−" : ""}{fmtMoney(m.stripe_fees, cur)}</td>
            <td className="px-3 py-2 text-zinc-500">{m.sovsig_fee ? "−" : ""}{fmtMoney(m.sovsig_fee, cur)}</td>
            <td className="px-3 py-2 text-zinc-500">{fmtMoney(m.refunds, cur)}</td>
            <td className="px-3 py-2 text-[#14A89A]">{fmtMoney(m.net, cur)}</td>
            <td className="px-3 py-2">{m.new_subs}</td>
            <td className="px-3 py-2">{m.canceled_subs}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const PAYOUT_STATUS = { paid: "text-[#14A89A]", in_transit: "text-amber-300", pending: "text-amber-300", failed: "text-red-400", canceled: "text-zinc-500" };

const PayoutsList = ({ payouts }) => payouts.length === 0
  ? <p className="font-body text-sm text-zinc-500" data-testid="earnings-no-payouts">No payouts yet. Stripe sends your balance to your bank automatically once it's available.</p>
  : (
    <ul className="divide-y divide-white/5 border border-white/10" data-testid="earnings-payouts">
      {payouts.map((p) => (
        <li key={p.id} className="flex items-center justify-between gap-3 px-4 py-3 font-body text-sm" data-testid={`payout-${p.id}`}>
          <span className="text-zinc-300">{fmtDate(p.arrival_date)}</span>
          <span className="text-white">{fmtMoney(p.amount, p.currency)}</span>
          <span className={`text-xs capitalize ${PAYOUT_STATUS[p.status] || "text-zinc-400"}`}>{p.status.replace("_", " ")}</span>
        </li>
      ))}
    </ul>
  );

const SubscribersList = ({ subs, navigate }) => subs.length === 0
  ? <p className="font-body text-sm text-zinc-500" data-testid="earnings-no-subscribers">No subscribers yet.</p>
  : (
    <ul className="divide-y divide-white/5 border border-white/10" data-testid="earnings-subscribers">
      {subs.map((s) => (
        <li key={s.id} className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white/[0.02]" onClick={() => navigate(`/profile/${s.id}`)} data-testid={`earnings-sub-${s.id}`}>
          <Avatar name={s.name} src={s.avatar} iv={s.avatar_iv} size={32} />
          <div className="min-w-0 flex-1">
            <div className="font-body text-sm text-white truncate">{s.name}</div>
            <div className="font-body text-[11px] text-zinc-500">Since {fmtDate(s.since)}</div>
          </div>
          <span className={`font-body text-xs shrink-0 ${s.status === "canceling" ? "text-amber-300" : "text-[#14A89A]"}`}>
            {s.status === "canceling" ? `Cancels ${fmtDate(s.renews_at)}` : "Active"}
          </span>
        </li>
      ))}
    </ul>
  );

export default function BeaconEarnings() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/beacon/earnings").then(({ data }) => setD(data)).catch((e) => setErr(e?.response?.data?.detail || "Couldn't load earnings."));
  }, []);

  if (err) return <div className="max-w-4xl mx-auto px-4 py-12 font-body text-sm text-zinc-400" data-testid="earnings-error">{err}</div>;
  if (!d) return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-[#14A89A]" /></div>;
  const cur = d.currency;
  const thisMonth = d.months[0];

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 space-y-10" data-testid="beacon-earnings-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <button onClick={() => navigate("/beacon")} data-testid="earnings-back" className="flex items-center gap-1.5 font-body text-xs text-zinc-500 hover:text-white mb-3"><ArrowLeft size={14} /> Back to Beacon</button>
          <h1 className="font-heading text-3xl font-medium tracking-tight text-[#14A89A]">Earnings</h1>
          <p className="font-body text-sm text-zinc-500 mt-1">Live from your Stripe account. Only you can see this.</p>
        </div>
        <button onClick={() => downloadCsv(d)} data-testid="earnings-download-csv" className="flex items-center gap-2 border border-white/15 text-zinc-200 px-4 py-2 font-body text-xs hover:bg-white/5"><Download size={14} /> Download CSV</button>
      </header>

      {d.stripe_error && <p className="font-body text-xs text-amber-300" data-testid="earnings-stripe-error">{d.stripe_error} Subscriber numbers below are still accurate.</p>}
      {!d.stripe_ready && !d.stripe_error && <p className="font-body text-xs text-amber-300" data-testid="earnings-not-connected">Connect payouts on your Beacon page to see revenue and payouts here.</p>}

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Users} label="Active subscribers" value={d.active_count + d.canceling_count} testid="earnings-stat-subscribers" />
        <Stat icon={TrendingUp} label="Net this month" value={fmtMoney(thisMonth.net, cur)} testid="earnings-stat-month" accent />
        <Stat icon={Clock} label="On its way" value={d.balance ? fmtMoney(d.balance.pending, cur) : "-"} testid="earnings-stat-pending" />
        <Stat icon={Wallet} label="Ready to pay out" value={d.balance ? fmtMoney(d.balance.available, cur) : "-"} testid="earnings-stat-available" />
      </section>

      <section>
        <h2 className="font-heading text-lg text-white mb-3">Last 6 months</h2>
        <MonthsTable months={d.months} cur={cur} />
      </section>

      <section>
        <h2 className="font-heading text-lg text-white mb-3">Payouts to your bank</h2>
        <PayoutsList payouts={d.payouts} />
      </section>

      <section>
        <h2 className="font-heading text-lg text-white mb-3">Subscribers</h2>
        <SubscribersList subs={d.subscribers} navigate={navigate} />
      </section>
    </div>
  );
}
